import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, Search, Copy, Edit2, Trash2, Tag, FileText, Import, ArrowUpDown, GripVertical, Image as ImageIcon, Link2, X, DollarSign, TrendingUp, Play } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import PageHeader, { AccountSelector, EmptyState } from '../../components/PageHeader';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api, { apiError, mediaUrl } from '../../lib/api';
import ScriptsAnalytics from './ScriptsAnalytics';
import { VaultPickerDialog } from './inbox/Pickers';
import { LightboxDialog } from './vault/Dialogs';
import { useAuth } from '../../context/AuthContext';

const TAG_COLORS = ['#6366f1', '#3b82f6', '#f43f5e', '#f59e0b', '#10b981', '#a78bfa', '#ec4899'];

export default function Scripts() {
  const { t } = useTranslation();
  const { hasPerm } = useAuth();
  const canManage = hasPerm('scripts_manage'); // chatters: view & copy only
  const [view, setView] = useState('library');
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState('');
  const [scripts, setScripts] = useState([]);
  const [tags, setTags] = useState([]);
  const [vault, setVault] = useState([]);
  const [tag, setTag] = useState('all');
  const [q, setQ] = useState('');
  const [reversed, setReversed] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [addTag, setAddTag] = useState(false);
  const [selected, setSelected] = useState(() => new Set()); // multi-select script ids
  const [bulkTag, setBulkTag] = useState('');                // tag to apply in bulk
  const [preview, setPreview] = useState(null);              // vault media opened in the lightbox
  const [dragId, setDragId] = useState(null);                // script being dragged
  const [overId, setOverId] = useState(null);                // drop target hovered

  useEffect(() => {
    api.get('/accounts').then(({ data }) => {
      setAccounts(data);
      if (data[0]) setAccount(prev => prev || data[0].id);
    }).catch(e => toast.error(apiError(e, t('dash.scripts.loadAccountsFailed'))));
  }, [t]);

  const load = useCallback(async (acc) => {
    if (!acc || acc === 'all') return;
    try {
      const [s, tg, v] = await Promise.all([
        api.get('/scripts', { params: { account_id: acc } }),
        api.get('/script-tags', { params: { account_id: acc } }),
        api.get('/vault', { params: { account_id: acc } }),
      ]);
      setScripts(s.data);
      setTags(tg.data);
      setVault(v.data);
    } catch (e) { toast.error(apiError(e, t('dash.scripts.loadScriptsFailed'))); }
  }, [t]);
  useEffect(() => { load(account); setTag('all'); setSelected(new Set()); }, [account, load]);

  const toggleSelect = (id) => setSelected(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const clearSelect = () => setSelected(new Set());

  const applyBulkTag = async () => {
    if (!bulkTag) { toast.error(t('dash.scripts.bulkTagRequired')); return; }
    const ids = [...selected];
    try {
      await api.post('/scripts/bulk-tag', { ids, tag: bulkTag });
      setScripts(prev => prev.map(s => ids.includes(s.id) ? { ...s, tag: bulkTag } : s));
      toast.success(t('dash.scripts.bulkTagToast', { count: ids.length, tag: bulkTag }));
      clearSelect(); setBulkTag('');
    } catch (e) { toast.error(apiError(e, t('dash.scripts.bulkTagFailed'))); }
  };

  const applyBulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(t('dash.scripts.bulkDeleteConfirm', { count: ids.length }))) return;
    try {
      await Promise.all(ids.map(id => api.delete(`/scripts/${id}`)));
      setScripts(prev => prev.filter(s => !ids.includes(s.id)));
      toast.success(t('dash.scripts.bulkDeleteToast', { count: ids.length }));
      clearSelect();
    } catch (e) { toast.error(apiError(e, t('dash.scripts.bulkDeleteFailed'))); load(account); }
  };

  // Drag-and-drop reorder. Only meaningful in the unfiltered, natural-order view
  // (so the on-screen order matches the underlying list we persist).
  const persistOrder = async (arr) => {
    try {
      await api.post('/scripts/reorder', { account_id: account, ordered_ids: arr.map(s => s.id) });
    } catch (e) { toast.error(apiError(e, t('dash.scripts.reorderFailed'))); load(account); }
  };
  const onDropScript = (targetId) => {
    const from = scripts.findIndex(s => s.id === dragId);
    const to = scripts.findIndex(s => s.id === targetId);
    setDragId(null); setOverId(null);
    if (from < 0 || to < 0 || from === to) return;
    const arr = [...scripts];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setScripts(arr);
    persistOrder(arr);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/scripts/${id}`);
      setScripts(scripts.filter(x => x.id !== id));
      toast.success(t('dash.scripts.scriptDeleted'));
    } catch (e) { toast.error(apiError(e, t('dash.scripts.deleteFailed'))); }
  };

  const removeTag = async (tagObj) => {
    try {
      await api.delete(`/script-tags/${tagObj.id}`);
      setTags(tags.filter(x => x.id !== tagObj.id));
      if (tag === tagObj.name) setTag('all');
      toast.success(t('dash.scripts.tagDeleted'));
    } catch (e) { toast.error(apiError(e, t('dash.scripts.deleteFailed'))); }
  };

  let filtered = scripts.filter(s => (tag === 'all' || s.tag === tag) && (s.title.toLowerCase().includes(q.toLowerCase()) || s.body.toLowerCase().includes(q.toLowerCase())));
  if (reversed) filtered = [...filtered].reverse();
  const reorderable = canManage && tag === 'all' && !q.trim() && !reversed;
  const allVisibleSelected = filtered.length > 0 && filtered.every(s => selected.has(s.id));
  const toggleSelectAll = () => allVisibleSelected ? clearSelect() : setSelected(new Set(filtered.map(s => s.id)));

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="scripts-page">
      <PageHeader
        title={t('dash.scripts.title')}
        subtitle={view === 'library' ? t('dash.scripts.subtitleLibrary', { tags: tags.length, scripts: scripts.length }) : t('dash.scripts.subtitleAnalytics')}
        actions={view === 'library' ? <AccountSelector value={account} onChange={setAccount} accounts={accounts} allowAll /> : null}
        onRefresh={view === 'library' ? () => load(account) : undefined}
      />

      <div className="inline-flex rounded-xl border border-white/10 bg-white/[0.03] p-1 mb-4" data-testid="scripts-view-tabs">
        <button onClick={() => setView('library')} data-testid="scripts-tab-library"
          className={`px-4 py-1.5 text-sm rounded-lg flex items-center gap-1.5 transition-all ${view === 'library' ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30' : 'text-slate-400 hover:text-white'}`}>
          <FileText size={14} /> {t('dash.scripts.tabLibrary')}
        </button>
        <button onClick={() => setView('analytics')} data-testid="scripts-tab-analytics"
          className={`px-4 py-1.5 text-sm rounded-lg flex items-center gap-1.5 transition-all ${view === 'analytics' ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30' : 'text-slate-400 hover:text-white'}`}>
          <TrendingUp size={14} /> {t('dash.scripts.tabAnalytics')}
        </button>
      </div>

      {view === 'analytics' ? <ScriptsAnalytics embedded /> : (
      <>
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="scripts-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder={t('dash.scripts.searchPlaceholder')} className="bg-white/5 border-white/10 text-white pl-9 h-10" />
        </div>
        {canManage && <Button variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-10" onClick={() => toast.info(t('dash.scripts.importComingSoon'))}><Import size={14} className="mr-1.5" /> {t('dash.scripts.import')}</Button>}
        {canManage && <Button data-testid="add-tag-button" variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-10" onClick={() => setAddTag(true)}><Tag size={14} className="mr-1.5" /> {t('dash.scripts.addTag')}</Button>}
        <Button data-testid="reverse-order-button" variant="outline" className={`border-white/10 h-10 hover:bg-white/10 ${reversed ? 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30' : 'bg-white/5 text-white'}`} onClick={() => setReversed(!reversed)}><ArrowUpDown size={14} className="mr-1.5" /> {t('dash.scripts.reverseOrder')}</Button>
        {canManage && <Button data-testid="new-script-button" className="bg-indigo-500 hover:bg-indigo-600 h-10" onClick={() => setCreating(true)}><Plus size={16} className="mr-1.5" /> {t('dash.scripts.newScript')}</Button>}
      </div>

      {selected.size > 0 && (
        <div data-testid="bulk-tag-bar" className="flex items-center gap-3 mb-4 rounded-xl border border-indigo-500/30 bg-indigo-500/10 px-4 py-2.5">
          <span className="text-sm text-white font-medium">{t('dash.scripts.selectedCount', { count: selected.size })}</span>
          <span className="text-xs text-slate-400">{t('dash.scripts.assignToTag')}</span>
          <select data-testid="bulk-tag-select" value={bulkTag} onChange={e => setBulkTag(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 h-8 text-xs text-white outline-none">
            <option value="" className="bg-[#0B0B0E]">{t('dash.scripts.pickTag')}</option>
            {tags.map(tg => <option key={tg.id} value={tg.name} className="bg-[#0B0B0E]">{tg.name}</option>)}
          </select>
          <Button data-testid="bulk-tag-apply" size="sm" className="h-8 bg-indigo-500 hover:bg-indigo-600" onClick={applyBulkTag}><Tag size={12} className="mr-1" /> {t('dash.scripts.apply')}</Button>
          <Button data-testid="bulk-delete-apply" size="sm" variant="outline" className="h-8 border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20" onClick={applyBulkDelete}><Trash2 size={12} className="mr-1" /> {t('dash.scripts.delete')}</Button>
          <button onClick={clearSelect} className="ml-auto text-xs text-slate-400 hover:text-white">{t('dash.scripts.deselectAll')}</button>
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {/* Tags sidebar */}
        <div className="col-span-3 rounded-2xl border border-white/10 bg-white/[0.02] p-3 h-fit">
          <div className="flex items-center justify-between px-2 mb-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t('dash.scripts.tags')}</span>
            <span className="text-[10px] text-slate-600">{tags.length}</span>
          </div>
          {canManage && (
            <button onClick={() => setAddTag(true)} className="w-full mb-2 px-3 py-2 rounded-lg border border-dashed border-white/15 text-xs text-slate-400 hover:text-white hover:bg-white/5 flex items-center justify-center gap-1.5">
              <Plus size={12} /> {t('dash.scripts.addTag')}
            </button>
          )}
          <div className="space-y-0.5">
            <button onClick={() => setTag('all')} className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between ${tag === 'all' ? 'bg-indigo-500/15 text-white border border-indigo-500/30' : 'text-slate-400 hover:bg-white/5 border border-transparent'}`}>
              <span>{t('dash.scripts.allScripts')}</span>
              <span className="text-[10px] text-slate-500">{scripts.length}</span>
            </button>
            {tags.map(tg => {
              const count = scripts.filter(s => s.tag === tg.name).length;
              return (
                <div key={tg.id} className={`group flex items-center rounded-lg ${tag === tg.name ? 'bg-indigo-500/15 border border-indigo-500/30' : 'border border-transparent hover:bg-white/5'}`}>
                  <button data-testid={`script-tag-${tg.name}`} onClick={() => setTag(tg.name)} className={`flex-1 text-left px-3 py-2 text-sm flex items-center gap-2 ${tag === tg.name ? 'text-white' : 'text-slate-400'}`}>
                    <GripVertical size={10} className="text-slate-700" />
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: tg.color }} />
                    <span className="truncate">{tg.name}</span>
                    <span className="ml-auto text-[10px] text-slate-500">{count}</span>
                  </button>
                  {canManage && <button data-testid={`delete-tag-${tg.id}`} onClick={() => removeTag(tg)} className="pr-2 text-slate-600 hover:text-rose-400 opacity-0 group-hover:opacity-100"><X size={11} /></button>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Scripts list */}
        <div className="col-span-9 space-y-2">
          {canManage && filtered.length > 0 && (
            <div className="flex items-center gap-2 px-1 pb-0.5">
              <Checkbox data-testid="select-all-scripts" className="border-white/20" checked={allVisibleSelected} onCheckedChange={toggleSelectAll} />
              <span className="text-[11px] text-slate-500">
                {selected.size > 0 ? `${t('dash.scripts.selectedCount', { count: selected.size })} · ` : t('dash.scripts.selectAllHint')}
                {reorderable ? t('dash.scripts.dragHint') : t('dash.scripts.reorderDisabled')}
              </span>
            </div>
          )}
          {filtered.length === 0 ? (
            <EmptyState icon={FileText} title={t('dash.scripts.emptyTitle')} desc={t('dash.scripts.emptyDesc')} />
          ) : filtered.map(s => {
            const thumbs = (s.vault_ids || []).map(id => vault.find(v => v.id === id)).filter(Boolean);
            return (
              <div key={s.id}
                draggable={reorderable}
                onDragStart={reorderable ? (() => setDragId(s.id)) : undefined}
                onDragOver={reorderable ? (e => { e.preventDefault(); if (overId !== s.id) setOverId(s.id); }) : undefined}
                onDragLeave={reorderable ? (() => setOverId(null)) : undefined}
                onDrop={reorderable ? (() => onDropScript(s.id)) : undefined}
                onDragEnd={() => { setDragId(null); setOverId(null); }}
                className={`group rounded-2xl border bg-white/[0.02] px-4 py-3.5 transition-colors flex items-start gap-3 ${selected.has(s.id) ? 'border-indigo-500/50 bg-indigo-500/[0.06]' : 'border-white/10 hover:border-indigo-500/30'} ${dragId === s.id ? 'opacity-40' : ''} ${overId === s.id && dragId && dragId !== s.id ? 'ring-2 ring-indigo-400/60' : ''}`}
                data-testid={`script-row-${s.id}`}>
                {canManage && (
                  <div className="flex items-center gap-2 pt-1 shrink-0">
                    <GripVertical size={13} className={reorderable ? 'text-slate-500 cursor-grab active:cursor-grabbing' : 'text-slate-800'} />
                    <Checkbox data-testid={`select-script-${s.id}`} className="border-white/20" checked={selected.has(s.id)} onCheckedChange={() => toggleSelect(s.id)} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white mb-1">{s.title}</div>
                  <div className="text-xs text-slate-400 line-clamp-2 mb-1.5">{s.body}</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {s.link && (
                      <span className="text-[10px] text-indigo-400 flex items-center gap-1 truncate max-w-[280px]"><Link2 size={9} /> {s.link}</span>
                    )}
                    {s.price > 0 && <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-300 text-[10px]">{s.price} $</Badge>}
                    <span className="text-[10px] text-slate-600">{t('dash.scripts.uses', { count: s.uses })}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {thumbs.length > 0 && (
                    <div className="flex gap-1">
                      {thumbs.slice(0, 3).map(v => (
                        <button key={v.id} type="button" draggable={false} onClick={(e) => { e.stopPropagation(); setPreview(v); }}
                          className="relative w-12 h-12 rounded-lg overflow-hidden border border-indigo-500/30 bg-[#101013] hover:ring-2 hover:ring-indigo-400/60"
                          title={t('dash.scripts.thumbTitle', { name: v.name })}>
                          {v.url ? <img src={mediaUrl(v.url)} alt={v.name} className="w-full h-full object-cover" loading="lazy" /> : <span className="absolute inset-0 flex items-center justify-center text-[7px] text-slate-500 px-1 text-center">{v.name}</span>}
                          {v.type === 'video' && <span className="absolute inset-0 flex items-center justify-center pointer-events-none"><Play size={13} className="text-white drop-shadow" fill="currentColor" /></span>}
                        </button>
                      ))}
                      {thumbs.length > 3 && <span className="self-center text-[10px] text-slate-500">+{thumbs.length - 3}</span>}
                    </div>
                  )}
                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:bg-white/5" onClick={() => { navigator.clipboard?.writeText(s.body + (s.link ? `\n${s.link}` : '')); toast.success(t('dash.scripts.copied')); }}><Copy size={11} /></Button>
                    {canManage && <Button data-testid={`edit-script-${s.id}`} size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:bg-white/5" onClick={() => setEditing(s)}><Edit2 size={11} /></Button>}
                    {canManage && <Button data-testid={`delete-script-${s.id}`} size="icon" variant="ghost" className="h-6 w-6 text-rose-400 hover:bg-rose-500/10" onClick={() => remove(s.id)}><Trash2 size={11} /></Button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ScriptDialog
        open={creating || !!editing}
        editing={editing}
        account={account}
        tags={tags}
        vault={vault}
        defaultTag={tag !== 'all' ? tag : (tags[0]?.name || 'General')}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={() => { setCreating(false); setEditing(null); load(account); }}
      />
      <AddTagDialog open={addTag} account={account} onClose={() => setAddTag(false)} onCreated={(newTag) => { setTags([...tags, newTag]); setAddTag(false); }} />
      <LightboxDialog preview={preview} onClose={() => setPreview(null)} tagColors={{}} />
      </>
      )}
    </div>
  );
}

function AddTagDialog({ open, account, onClose, onCreated }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [color, setColor] = useState(TAG_COLORS[0]);
  const save = async () => {
    if (!name.trim()) { toast.error(t('dash.scripts.tagNameRequired')); return; }
    try {
      const { data } = await api.post('/script-tags', { account_id: account, name: name.trim(), color });
      setName('');
      toast.success(t('dash.scripts.tagAdded'));
      onCreated(data);
    } catch (e) { toast.error(apiError(e, t('dash.scripts.addTagFailed'))); }
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-sm">
        <DialogHeader><DialogTitle className="text-white">{t('dash.scripts.addTagTitle')}</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <Input data-testid="tag-name-input" value={name} onChange={e => setName(e.target.value)} className="bg-white/5 border-white/10 text-white h-10" placeholder={t('dash.scripts.tagNamePlaceholder')} />
          <div className="flex gap-2">
            {TAG_COLORS.map(c => (
              <button key={c} onClick={() => setColor(c)} className={`w-7 h-7 rounded-full border-2 ${color === c ? 'border-white' : 'border-transparent'}`} style={{ background: c }} />
            ))}
          </div>
          <Button data-testid="tag-save-button" className="w-full bg-indigo-500 hover:bg-indigo-600" onClick={save}>{t('dash.scripts.add')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScriptDialog({ open, editing, account, tags, vault, defaultTag, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ title: '', tag: defaultTag, body: '', link: '', price: '', vault_ids: [] });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [lbPreview, setLbPreview] = useState(null); // vault media opened large
  const [saving, setSaving] = useState(false);
  const isEdit = !!editing;

  useEffect(() => {
    if (open) {
      if (editing) setForm({ title: editing.title, tag: editing.tag, body: editing.body, link: editing.link || '', price: editing.price || '', vault_ids: editing.vault_ids || [] });
      else setForm({ title: '', tag: defaultTag, body: '', link: '', price: '', vault_ids: [] });
    }
  }, [open, editing, defaultTag]);

  const save = async () => {
    if (!form.title) { toast.error(t('dash.scripts.titleContentRequired')); return; }
    setSaving(true);
    const payload = { title: form.title, tag: form.tag, body: form.body, link: form.link || null, price: parseFloat(form.price) || 0, vault_ids: form.vault_ids };
    try {
      if (isEdit) {
        await api.patch(`/scripts/${editing.id}`, payload);
        toast.success(t('dash.scripts.scriptUpdated'));
      } else {
        await api.post('/scripts', { ...payload, account_id: account });
        toast.success(t('dash.scripts.scriptCreated'));
      }
      onSaved();
    } catch (e) { toast.error(apiError(e, t('dash.scripts.saveFailed'))); } finally { setSaving(false); }
  };

  const selectedMedia = form.vault_ids.map(id => vault.find(v => v.id === id)).filter(Boolean);

  return (
    <>
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-lg max-h-[88vh] overflow-y-auto scrollbar-thin">
        <DialogHeader><DialogTitle className="text-white">{isEdit ? t('dash.scripts.editScript') : t('dash.scripts.newScriptTitle')}</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><Tag size={11} /> {t('dash.scripts.titleLabel')} <span className="text-rose-400">*</span></Label>
            <Input data-testid="script-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" placeholder={t('dash.scripts.titlePlaceholder')} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><FileText size={11} /> {t('dash.scripts.contentLabel')}</Label>
            <Textarea data-testid="script-body-input" value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} className="bg-white/5 border-white/10 text-white min-h-[110px]" placeholder={t('dash.scripts.contentPlaceholder')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">{t('dash.scripts.tagLabel')}</Label>
              <select value={form.tag} onChange={e => setForm({ ...form, tag: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white h-10 outline-none">
                {tags.length === 0 && <option value={form.tag} className="bg-[#0B0B0E]">{form.tag}</option>}
                {tags.map(tg => <option key={tg.id} value={tg.name} className="bg-[#0B0B0E]">{tg.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs flex items-center gap-1.5"><DollarSign size={11} /> {t('dash.scripts.priceLabel')}</Label>
              <Input data-testid="script-price-input" type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" placeholder={t('dash.scripts.pricePlaceholder')} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><Link2 size={11} /> {t('dash.scripts.linkLabel')}</Label>
            <Input data-testid="script-link-input" value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} className="bg-white/5 border-white/10 text-white h-10 font-mono text-xs" placeholder={t('dash.scripts.linkPlaceholder')} />
            <p className="text-[10px] text-slate-500">{t('dash.scripts.linkHelp')}</p>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300 text-xs flex items-center gap-1.5"><ImageIcon size={11} /> {t('dash.scripts.vaultMediaLabel')}</Label>
              <Button data-testid="choose-from-vault-button" variant="outline" size="sm" className="h-7 text-[11px] border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={() => setPickerOpen(true)}><ImageIcon size={11} className="mr-1" /> {t('dash.scripts.chooseFromVault')}</Button>
            </div>
            {selectedMedia.length > 0 ? (
              <div className="flex gap-2 flex-wrap">
                {selectedMedia.map(v => (
                  <div key={v.id} className="relative">
                    <button type="button" onClick={() => setLbPreview(v)} title={t('dash.scripts.mediaEnlarge', { name: v.name })}
                      className="relative w-14 h-14 rounded-lg overflow-hidden border border-indigo-500/40 bg-[#101013] block hover:ring-2 hover:ring-indigo-400/60">
                      {v.url ? <img src={mediaUrl(v.url)} alt={v.name} className="w-full h-full object-cover" /> : <span className="absolute inset-0 flex items-center justify-center text-[7px] text-slate-400 text-center px-1">{v.name}</span>}
                      {v.type === 'video' && <span className="absolute inset-0 flex items-center justify-center pointer-events-none"><Play size={14} className="text-white drop-shadow" fill="currentColor" /></span>}
                    </button>
                    <button onClick={() => setForm({ ...form, vault_ids: form.vault_ids.filter(x => x !== v.id) })} className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-slate-800 border border-white/20 text-white flex items-center justify-center z-10"><X size={9} /></button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-slate-500">{t('dash.scripts.noMedia')}</p>
            )}
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={onClose}>{t('dash.scripts.cancel')}</Button>
            <Button data-testid="script-save-button" className="bg-indigo-500 hover:bg-indigo-600" onClick={save} disabled={saving}>{saving ? t('dash.scripts.saving') : isEdit ? t('dash.scripts.saveChanges') : t('dash.scripts.createScript')}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <VaultPickerDialog
      open={pickerOpen}
      onClose={() => setPickerOpen(false)}
      vault={vault}
      sentIds={[]}
      onPick={(picked) => {
        const ids = (picked || []).map(p => p.id);
        setForm(f => ({ ...f, vault_ids: Array.from(new Set([...f.vault_ids, ...ids])) }));
        setPickerOpen(false);
      }}
    />
    <LightboxDialog preview={lbPreview} onClose={() => setLbPreview(null)} tagColors={{}} />
    </>
  );
}
